import moment from 'moment-jalaali';
import { getDateRangePickerValue, parseDateRangeChange } from '../pages/reports/reportUtils';

export const persianToEnglishDigits = (str) => {
  if (!str) return str;
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  let result = String(str);
  persianDigits.forEach((p, i) => {
    result = result.replace(new RegExp(p, 'g'), englishDigits[i]);
  });
  return result;
};

const pad2 = (n) => String(n).padStart(2, '0');

const isDateObjectLike = (value) => {
  if (!value || typeof value !== 'object') return false;
  if (moment.isMoment(value)) return false;
  if (typeof value.year === 'function' || typeof value.month === 'function' || typeof value.day === 'function') {
    return false;
  }
  return value.year != null && value.month != null && value.day != null;
};

/** فرمت استاندارد API: 1405/03/04 */
export const formatJalaliDateForApi = (value) => {
  if (value == null || value === '') return undefined;

  if (typeof value === 'string') {
    const trimmed = persianToEnglishDigits(value).trim();
    if (!trimmed || trimmed.includes('function')) return undefined;
    let normalized = trimmed.replace(/^j/i, '').replace(/-/g, '/');
    const parts = normalized.split('/').filter(Boolean);
    if (parts.length === 3) {
      const [y, m, d] = parts;
      return `${y}/${pad2(m)}/${pad2(d)}`;
    }
    return normalized || undefined;
  }

  if (typeof value === 'number' && !Number.isNaN(value)) {
    const m = moment(value);
    if (m.isValid()) {
      return persianToEnglishDigits(m.format('jYYYY/jMM/jDD'));
    }
    return undefined;
  }

  if (moment.isMoment(value)) {
    return value.isValid() ? persianToEnglishDigits(value.format('jYYYY/jMM/jDD')) : undefined;
  }

  if (isDateObjectLike(value)) {
    const monthNum = typeof value.month === 'object' ? value.month.number : value.month;
    const raw = `${value.year}/${pad2(monthNum)}/${pad2(value.day)}`;
    return persianToEnglishDigits(raw);
  }

  if (value instanceof Date) {
    const m = moment(value);
    return m.isValid() ? persianToEnglishDigits(m.format('jYYYY/jMM/jDD')) : undefined;
  }

  if (typeof value.format === 'function' && !moment.isMoment(value)) {
    try {
      const raw = value.format('YYYY/MM/DD');
      const normalized = persianToEnglishDigits(String(raw)).replace(/-/g, '/');
      const parts = normalized.split('/').filter(Boolean);
      if (parts.length === 3) {
        const [y, m, d] = parts;
        return `${y}/${pad2(m)}/${pad2(d)}`;
      }
      return normalized || undefined;
    } catch {
      /* fall through */
    }
  }

  if (typeof value.toDate === 'function') {
    const m = moment(value.toDate());
    if (m.isValid()) {
      return persianToEnglishDigits(m.format('jYYYY/jMM/jDD'));
    }
  }

  return undefined;
};

/** بازه پیش‌فرض: اول تا آخر ماه جاری شمسی */
export const getDefaultWorkDateRange = () => {
  const start = moment().startOf('jMonth');
  const end = moment().endOf('jMonth');
  return {
    startDate: formatJalaliDateForApi(start),
    endDate: formatJalaliDateForApi(end),
  };
};

/**
 * فیلتر لیست — همیشه startDate و endDate به صورت رشته شمسی
 */
export const buildWorkListFilters = (filters = {}, searchText = '', defaults = null) => {
  const range = defaults || getDefaultWorkDateRange();
  const { startDate, endDate, ...rest } = filters;

  const api = {
    ...rest,
    startDate: formatJalaliDateForApi(startDate) || range.startDate,
    endDate: formatJalaliDateForApi(endDate) || range.endDate,
  };

  if (searchText) api.search = searchText;
  else delete api.search;

  Object.keys(api).forEach((k) => {
    if (api[k] === '' || api[k] === undefined) {
      if (k !== 'startDate' && k !== 'endDate') delete api[k];
    }
  });

  return api;
};

/** فیلتر بازه تاریخ برای StandardFilterPanel (همیشه startDate + endDate با هم) */
export const createWorkDateRangeFilter = (startDate, endDate) => ({
  key: 'date_range',
  type: 'dateRange',
  label: 'بازه تاریخ',
  value: getDateRangePickerValue(startDate, endDate),
  placeholder: 'انتخاب بازه از تاریخ تا تاریخ',
  allowClear: false,
});

/** onChange بازه تاریخ — فقط وقتی هر دو تاریخ انتخاب شد اعمال می‌شود */
export const applyWorkDateRangeChange = (dates, defaultRange) => {
  const next = parseDateRangeChange(dates);
  if (!next.startDate || !next.endDate) return null;
  return {
    startDate: formatJalaliDateForApi(next.startDate) || defaultRange.startDate,
    endDate: formatJalaliDateForApi(next.endDate) || defaultRange.endDate,
  };
};
